/**
 * The session, and the guard that keeps unauthenticated views from rendering.
 *
 * The access token lives in **memory only**. A token in `localStorage` is a token any
 * cross-site scripting reads, and this application renders text that a research subject
 * wrote — so the assumption that XSS cannot happen is not one worth taking. The refresh
 * token is an `HttpOnly` cookie the browser sends and JavaScript never sees; a page
 * reload silently re-establishes the session from it.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import {
  ApiError,
  isSessionIdle,
  maybeProactiveRefresh,
  request,
  setAccessToken,
  setAuthLostHandler,
  setIdleWindowMs,
} from '@/lib/api';
import type { LoginRequest, MfaVerifyRequest, Session } from '@/lib/schemas';
import { Loading } from '@/components/ui';

interface SessionContextValue {
  session: Session | null;
  /** Whether the initial refresh attempt has finished. */
  ready: boolean;
  signIn: (tenantSlug: string, email: string, password: string) => Promise<Session>;
  submitMfa: (code: string) => Promise<Session>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

/** Provide the session to the tree. */
export function SessionProvider({ children }: { children: ReactNode }): ReactElement {
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);

  /**
   * The challenge the password step issued, held between `signIn` and `submitMfa`.
   *
   * **`POST /auth/mfa/verify` requires it**, and nothing captured it: the provider sent
   * only the code, so the request failed validation and no amount of correct codes could
   * complete a login. Together with the login screen matching on the wrong status, this
   * meant enrolling in two-factor authentication locked you out of the browser for good.
   *
   * A ref rather than state: it must not cause a render, and it authorises nothing on its
   * own — it names the user whose password already checked out, and expires in minutes.
   */
  const challenge = useRef<string | null>(null);

  const adopt = useCallback((next: Session) => {
    setAccessToken(next.access_token);
    // Mirror the server's idle window so the client enforces the same bound rather than a
    // hard-coded second copy: sign out on inactivity, and stop refreshing once idle.
    setIdleWindowMs(next.idle_timeout_seconds * 1000);
    setSession(next);
    return next;
  }, []);

  const signOut = useCallback(async () => {
    await request<void>('/auth/logout', { method: 'POST' }).catch(() => undefined);
    setIdleWindowMs(0);
    setAccessToken(null);
    setSession(null);
  }, []);

  // **The API layer renews the token on its own now**, because the access token lives
  // fifteen minutes and this provider only ever refreshed once, at mount — so a session
  // died mid-click at the quarter hour however active the user was. When even the refresh
  // cookie cannot recover it, the session is genuinely over and the shell has to know.
  useEffect(() => {
    setAuthLostHandler(() => setSession(null));
    return () => setAuthLostHandler(null);
  }, []);

  useEffect(() => {
    // A reload has no token in memory but may still have the refresh cookie. Trying
    // once, and treating failure as "signed out", is what makes a refresh feel like a
    // continuation rather than a logout.
    void request<Session>('/auth/refresh', { method: 'POST' })
      .then(adopt)
      .catch(() => setSession(null))
      .finally(() => setReady(true));
  }, [adopt]);

  // **The idle timeout, enforced — and kept alive while active.** While signed in, a
  // coarse timer does two things. If the user has been idle past the window the server
  // set, it signs them out — which also revokes the refresh family, so the session is over
  // on the server too, not merely hidden in this tab. Otherwise, if the user is active but
  // the token is ageing, it refreshes proactively: this is what makes activity — not the
  // access-token clock — drive the sliding window, so an active session no longer times
  // out early when no background request happened to renew it. The API layer refuses to
  // refresh during the idle period, so nothing keeps an idle session alive underneath this.
  useEffect(() => {
    if (!session) return undefined;
    const interval = window.setInterval(() => {
      if (isSessionIdle()) {
        void signOut();
        return;
      }
      void maybeProactiveRefresh();
    }, 15_000);
    return () => window.clearInterval(interval);
  }, [session, signOut]);

  const value = useMemo<SessionContextValue>(
    () => ({
      session,
      ready,
      signIn: async (tenantSlug, email, password) => {
        try {
          return adopt(
            await request<Session, LoginRequest>('/auth/login', {
              method: 'POST',
              // The tenant is part of the credential, not a lookup from the address. Two
              // tenants may legitimately have a user with the same email, and resolving by
              // address alone would make which account you got depend on insertion order.
              body: { tenant_slug: tenantSlug, email, password },
            }),
          );
        } catch (caught) {
          // The password was accepted and the second factor is owed. Keep the challenge
          // and rethrow, so the screen can ask for a code.
          if (caught instanceof ApiError && caught.code === 'MFA_REQUIRED') {
            const detail = caught.detail;
            challenge.current =
              detail !== null && typeof detail === 'object' && 'challenge_token' in detail
                ? String(detail.challenge_token)
                : null;
          }
          throw caught;
        }
      },
      submitMfa: async (code) => {
        if (challenge.current === null) {
          throw new Error('No sign-in is awaiting a second factor. Start again.');
        }
        const next = adopt(
          await request<Session, MfaVerifyRequest>('/auth/mfa/verify', {
            method: 'POST',
            body: { challenge_token: challenge.current, code },
          }),
        );
        // Single use. Holding it after it has been spent would let a stale challenge be
        // replayed against a later, unrelated attempt.
        challenge.current = null;
        return next;
      },
      signOut,
    }),
    [session, ready, adopt, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/** The current session, or `null` when signed out. */
export function useSession(): Session | null {
  return useSessionContext().session;
}

/** The session context, including the sign-in actions. */
export function useSessionContext(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSessionContext must be used inside a SessionProvider');
  return value;
}

/**
 * Render children only for a signed-in principal.
 *
 * Not a security control — the server authorises every request. This stops the
 * application rendering a case workspace that would immediately fill with 401s.
 */
export function RequireSession({ children }: { children: ReactNode }): ReactElement {
  const { session, ready } = useSessionContext();
  const location = useLocation();

  if (!ready) return <Loading label="Restoring your session" />;
  if (!session) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return <>{children}</>;
}
